import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

const API_USER_LIST_DEFAULT_PAGE_SIZE = 50;

let initialApiUserListRange = null;
let initialApiUserListRangeUsesLeft = 0;

const apiUserUrl = (row) => {
  if (
    row?.id === null
    || row?.id === undefined
  ) {
    throw new Error(
      "apiUser.id is required to build the form URL"
    );
  }

  return `/__partner-user/${row.id}`;
};

const escapeRsqlRegexValue = (value) => {
  return String(value || "")
    .trim()
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[.*+?^{}$()|[\]\\]/g, "\\$&");
};

const buildApiUserSearchQuery = (search) => {
  const rawValue =
    String(search || "").trim();

  const regexValue =
    escapeRsqlRegexValue(rawValue);

  if (!regexValue) {
    return "";
  }

  const parts = [
    `description=re="(?i).*${regexValue}.*"`
  ];

  if (/^\d+$/.test(rawValue)) {
    parts.push(
      `id==${Number(rawValue)}`
    );
  }

  return parts.join(",");
};

const readApiUserListRangeFromUrl = () => {
  const params =
    new URLSearchParams(
      window.location.search
    );

  const offset =
    Number(params.get("offset"));

  const limit =
    Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) && limit > 0
      ? Math.floor(limit)
      : API_USER_LIST_DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) && offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page:
      Math.floor(
        safeOffset / safeLimit
      ),
    pageSize: safeLimit,
    hasExplicitRange:
      params.has("offset")
      || params.has("limit")
  };
};

const writeApiUserListRangeToUrl = ({
  offset,
  limit
}) => {
  const url =
    new URL(window.location.href);

  url.searchParams.set(
    "offset",
    String(offset)
  );

  url.searchParams.set(
    "limit",
    String(limit)
  );

  window.history.replaceState(
    {},
    "",
    url.toString()
  );
};

const getEffectiveApiUserListQuery = (
  query
) => {
  const search =
    String(query?.search || "").trim();

  const sort =
    Array.isArray(query?.sort)
      ? query.sort
      : [];

  const page =
    Number.isFinite(query?.page)
      ? query.page
      : 0;

  const pageSize =
    Number.isFinite(query?.pageSize)
      ? query.pageSize
      : API_USER_LIST_DEFAULT_PAGE_SIZE;

  if (
    initialApiUserListRange
    && initialApiUserListRange.hasExplicitRange
    && initialApiUserListRangeUsesLeft > 0
    && !search
    && page === 0
    && pageSize === API_USER_LIST_DEFAULT_PAGE_SIZE
  ) {
    initialApiUserListRangeUsesLeft -= 1;

    return {
      page:
        initialApiUserListRange.page,
      pageSize:
        initialApiUserListRange.pageSize,
      offset:
        initialApiUserListRange.offset,
      limit:
        initialApiUserListRange.limit,
      search,
      sort
    };
  }

  initialApiUserListRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset:
      page * pageSize,
    limit:
      pageSize,
    search,
    sort
  };
};

const selectorsForSortItem = (
  sortItem
) => {
  const column =
    sortItem?.column;

  if (
    Array.isArray(
      column?.sortSelectors
    )
  ) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [
      column.sortSelector
    ];
  }

  if (sortItem?.field) {
    return [
      sortItem.field
    ];
  }

  throw new Error(
    "Invalid sort item: missing sort selector"
  );
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (
      sortItem?.direction !== "asc"
      && sortItem?.direction !== "desc"
    ) {
      throw new Error(
        `Invalid sort direction: ${sortItem?.direction}`
      );
    }

    for (
      const selector
      of selectorsForSortItem(sortItem)
    ) {
      if (!selector) {
        throw new Error(
          `Invalid sort selector for field: ${sortItem?.field}`
        );
      }

      out.push({
        selector,
        direction:
          sortItem.direction
      });
    }
  }

  return out;
};

const buildApiUserSearchPayload = (
  range
) => {
  return {
    columns: [],
    query:
      buildApiUserSearchQuery(
        range.search
      ),
    limit:
      range.limit,
    offset:
      range.offset,
    sort:
      buildSortPayload(
        range.sort
      )
  };
};

const buildApiUserCountPayload = (
  query
) => {
  return {
    columns: [],
    query,
    limit: null,
    offset: null,
    sort: []
  };
};

const renderDescription = (row, gui) => {
  const label = row.description || `${t("user.apiUser")} ${row.id}`;

  if (!can(gui, "READ_PARTNER_USER")) {
    return <span>{label}</span>;
  }

  return (
    <a
      href={apiUserUrl(row)}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {label}
    </a>
  );
};

const renderViewAction = (
  row,
  gui
) => {
  if (!can(gui, "READ_PARTNER_USER")) {
    return null;
  }

  return (
    <Tooltip title={t("user.viewApiUser")}>
      <IconButton
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          gui.goTo(
            apiUserUrl(row)
          );
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const createApiUser = async (
  gui
) => {
  if (!can(gui, "CREATE_PARTNER_USER")) {
    return;
  }

  const id =
    await rest.request(
      getServiceUri()
        + "__partner-users",
      "POST",
      {
        description: ""
      }
    );

  if (
    id === null
    || id === undefined
  ) {
    throw new Error(
      "API user creation did not return an ID"
    );
  }

  if (can(gui, "READ_PARTNER_USER")) {
    gui.goTo(`/__partner-user/${id}`);
  } else {
    window.location.reload();
  }
};

const apiUserListProjection = {
  storageKey:
    "users.partnerUser",

  columns: [
    {
      title:
        "common.id",
      field:
        "id",
      sortSelector:
        "id"
    },

    {
      title:
        "common.description",
      field:
        "description",
      sortSelector:
        "description",
      render:
        renderDescription
    },

    {
      title:
        "common.actions",
      field:
        "__actions",
      sorting:
        false,
      filtering:
        false
    }
  ],

  actions: (
    row,
    gui
  ) =>
    renderViewAction(
      row,
      gui
    ),

  toolbarActions: (
    gui
  ) => (
    can(gui, "CREATE_PARTNER_USER") ? (
      <Tooltip title={t("user.newApiUser")}>
      <IconButton
        size="small"
        onClick={() => {
          createApiUser(gui)
            .catch((error) => {
              console.error(
                "API user creation failed",
                error
              );
            });
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
      </Tooltip>
    ) : null
  ),

  fetch: async (query) => {
    const range =
      getEffectiveApiUserListQuery(
        query
      );

    writeApiUserListRangeToUrl({
      offset:
        range.offset,
      limit:
        range.limit
    });

    const payload =
      buildApiUserSearchPayload(
        range
      );

    const [
      users,
      totalCount
    ] =
      await Promise.all([
        rest.request(
          getServiceUri()
            + "__partner-users/search",
          "POST",
          payload
        ),

        rest.request(
          getServiceUri()
            + "__partner-users/count",
          "POST",
          buildApiUserCountPayload(
            payload.query
          )
        )
      ]);

    return {
      data:
        users || [],
      totalCount:
        Number.isFinite(
          Number(totalCount)
        )
          ? Number(totalCount)
          : 0,
      page:
        range.page
    };
  }
};

export const displayPartnerUserList = (gui) => {
  initialApiUserListRange =
    readApiUserListRangeFromUrl();

  initialApiUserListRangeUsesLeft =
    initialApiUserListRange.hasExplicitRange
      ? 3
      : 0;

  return displayList(gui, {
    ...apiUserListProjection,

    beforeNavigationControl: () => (
      can(gui, "CREATE_PARTNER_USER") ? (
        <Tooltip title={t("user.newApiUser")}>
          <IconButton
            size="small"
            onClick={() => {
              createApiUser(gui).catch((error) => {
                console.error(
                  "API user creation failed",
                  error
                );
              });
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null
    )
  });
};

export default displayPartnerUserList;